/**
 * CampaignTemplatePickerDialog — pick a saved marketing template before
 * the campaign / ad-hoc send wizard (replaces static JSON starters).
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Skeleton,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import { useNavigate } from "react-router-dom";
import apiService from "../../../services/api";
import {
  API_TO_UI_CATEGORY,
  formFromApiRow,
  isMarketingNurtureTemplate,
  MARKETING_TEMPLATE_TABS,
  parseEmailTemplatesListResponse,
  templateMatchesCategory,
} from "./marketingTemplateUtils";
import type { SelectedEmailTemplate } from "./campaignStarterUtils";

interface TemplateListRow {
  id: string;
  template_key: string;
  template_name: string;
  category: string;
  status: string;
  is_active: boolean;
  subject_line?: string;
}

export interface CampaignTemplatePickerDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (template: SelectedEmailTemplate) => void;
  onBlankCanvas: () => void;
  /** Modal title override */
  title?: string;
}

const TEMPLATES_ADMIN_PATH = "/dashboard/communications/marketing/templates";

export const CampaignTemplatePickerDialog: React.FC<
  CampaignTemplatePickerDialogProps
> = ({
  open,
  onClose,
  onSelect,
  onBlankCanvas,
  title = "Choose an email template",
}) => {
  const navigate = useNavigate();
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [templates, setTemplates] = useState<TemplateListRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pickingId, setPickingId] = useState<string | null>(null);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.get(
        "/api/admin/email-templates?limit=200",
      );
      const rows = parseEmailTemplatesListResponse(
        res as { data?: unknown; templates?: unknown },
      ) as TemplateListRow[];
      const nurture = rows.filter((r) => isMarketingNurtureTemplate(r));
      setTemplates(nurture);
    } catch (e: unknown) {
      setError(
        e instanceof Error ? e.message : "Failed to load marketing templates",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      loadTemplates();
    } else {
      setCategory("all");
      setSearch("");
      setPickingId(null);
    }
  }, [open, loadTemplates]);

  const filtered = useMemo(() => {
    let rows = templates;
    if (category !== "all") {
      rows = rows.filter((r) => templateMatchesCategory(r.category, category));
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter(
        (r) =>
          r.template_key.toLowerCase().includes(q) ||
          (r.template_name?.toLowerCase() ?? "").includes(q) ||
          (r.subject_line?.toLowerCase() ?? "").includes(q),
      );
    }
    return rows.sort((a, b) =>
      (a.template_name || a.template_key).localeCompare(
        b.template_name || b.template_key,
      ),
    );
  }, [templates, category, search]);

  const handlePick = async (row: TemplateListRow) => {
    setPickingId(row.id);
    setError(null);
    try {
      const res = await apiService.get<Record<string, unknown>>(
        `/api/admin/email-templates/${row.id}`,
      );
      const data = (res.data ?? res) as Record<string, unknown>;
      const form = formFromApiRow(data);
      const selected: SelectedEmailTemplate = {
        id: row.id,
        template_key: form.template_key,
        template_name: form.template_name,
        subject_line: form.subject_line,
        html_content: form.html_content,
        plain_text_content: form.plain_text_content,
        category:
          API_TO_UI_CATEGORY[form.category] || form.category,
      };
      onSelect(selected);
      onClose();
    } catch (e: unknown) {
      setError(
        e instanceof Error ? e.message : "Failed to load template details",
      );
    } finally {
      setPickingId(null);
    }
  };

  const categoryLabel = (cat: string) => {
    const tab = MARKETING_TEMPLATE_TABS.find(
      (t) => t.id === cat || t.legacyMatch?.includes(cat),
    );
    return tab?.label ?? cat;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="campaign-template-picker-title"
    >
      <DialogTitle
        id="campaign-template-picker-title"
        sx={{ display: "flex", alignItems: "flex-start", gap: 1, pr: 1 }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Templates from Marketing Templates — same designs you manage under
            Build → Marketing Templates.
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Close" size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 1 }}>
        <Tabs
          value={category}
          onChange={(_, v) => setCategory(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
        >
          {MARKETING_TEMPLATE_TABS.map((c) => (
            <Tab
              key={c.id}
              value={c.id}
              label={c.label}
              sx={{ fontSize: "0.8rem", fontWeight: 600, minHeight: 40 }}
            />
          ))}
        </Tabs>

        <TextField
          fullWidth
          size="small"
          placeholder="Search by name, key, or subject…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Grid container spacing={2}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Grid item xs={12} sm={6} key={i}>
                <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 1 }} />
              </Grid>
            ))}
          </Grid>
        ) : filtered.length === 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            No active marketing templates in this category.{" "}
            <Button
              size="small"
              onClick={() => {
                onClose();
                navigate(TEMPLATES_ADMIN_PATH);
              }}
            >
              Create one in Marketing Templates
            </Button>
          </Alert>
        ) : (
          <Grid container spacing={1.5} sx={{ maxHeight: 360, overflowY: "auto", pb: 1 }}>
            {filtered.map((row) => {
              const busy = pickingId === row.id;
              return (
                <Grid item xs={12} sm={6} key={row.id}>
                  <Box
                    onClick={() => !busy && handlePick(row)}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      cursor: busy ? "wait" : "pointer",
                      opacity: busy ? 0.7 : 1,
                      "&:hover": busy
                        ? {}
                        : {
                            borderColor: "primary.main",
                            bgcolor: "action.hover",
                          },
                    }}
                  >
                    <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                      <EmailOutlinedIcon color="primary" sx={{ mt: 0.25 }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
                          {row.template_name || row.template_key}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary", fontFamily: "monospace" }}
                          noWrap
                        >
                          {row.template_key}
                        </Typography>
                        {row.subject_line && (
                          <Typography
                            variant="caption"
                            sx={{ display: "block", mt: 0.5, color: "text.secondary" }}
                            noWrap
                          >
                            Subject: {row.subject_line}
                          </Typography>
                        )}
                        <Box sx={{ mt: 1, display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                          <Chip
                            label={categoryLabel(
                              API_TO_UI_CATEGORY[row.category] || row.category,
                            )}
                            size="small"
                            sx={{ height: 20, fontSize: "0.65rem" }}
                          />
                          {row.status && row.status !== "active" && (
                            <Chip
                              label={row.status}
                              size="small"
                              color="warning"
                              variant="outlined"
                              sx={{ height: 20, fontSize: "0.65rem" }}
                            />
                          )}
                        </Box>
                      </Box>
                      {busy && <CircularProgress size={20} />}
                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        )}

        <Box
          sx={{
            mt: 2,
            pt: 2,
            borderTop: 1,
            borderColor: "divider",
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Button
            variant="outlined"
            onClick={() => {
              onBlankCanvas();
              onClose();
            }}
          >
            Start without a template
          </Button>
          <Button
            variant="text"
            size="small"
            onClick={() => {
              onClose();
              navigate(TEMPLATES_ADMIN_PATH);
            }}
          >
            Manage templates →
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CampaignTemplatePickerDialog;
